import { useEffect, useRef, useState } from 'react';
import type { PublicCan, PublicList } from '../../shared/api';
import { CanImage } from '../components/CanImage';
import { Sheet } from '../components/Sheet';
import { ZoomableCanImage } from '../components/ZoomableCanImage';
import { getFriendName, setFriendName } from '../api';
import { sizeLabel } from '../format';
import { countryLongName } from '../../shared/countries';
import { PublicScan, usePublicScan, wantedVariant } from './PublicScan';

interface Props {
  list: PublicList;
  /** Owner preview: shows "Sluit voorbeeld" and keeps claims local. */
  preview?: { onClose: () => void };
  onToggle?: (canId: number, reserve: boolean, friendName: string) => Promise<unknown>;
}

export function FriendView({ list, preview, onToggle }: Props) {
  const [localMine, setLocalMine] = useState<Map<number, boolean>>(new Map());
  const [askName, setAskName] = useState<number | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [pending, setPending] = useState(false);
  const busy = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const scan = usePublicScan(list.shareSlug);
  useEffect(() => setError(null), [scan.result?.ean]);
  const name = list.displayName;
  const n = list.missing.length;

  const isMine = (id: number, mine: boolean) => (preview ? (localMine.get(id) ?? mine) : mine);

  const toggle = async (canId: number, reserve: boolean, friendName?: string) => {
    if (busy.current) return;
    setError(null);
    if (preview) {
      setLocalMine((s) => {
        const next = new Map(s);
        next.set(canId, reserve);
        return next;
      });
      return;
    }
    const who = friendName ?? getFriendName();
    if (reserve && !who) {
      setAskName(canId);
      return;
    }
    busy.current = true;
    setPending(true);
    try {
      await onToggle?.(canId, reserve, who);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      await scan.refresh();
      busy.current = false;
      setPending(false);
    }
  };

  const groups = list.categories
    .map((c) => ({ cat: c, items: list.missing.filter((m) => m.categoryId === c.id) }))
    .filter((g) => g.items.length > 0);
  const selected = scanning
    ? scan.result?.data?.wanted?.id === selectedId
      ? scan.result.data.wanted
      : null
    : list.missing.find((m) => m.id === selectedId);
  const reservation = (item: PublicCan, allowed: boolean) => {
    const mine = isMine(item.id, item.mine);
    const other = !mine && !(preview && item.mine) && item.reservedBy;
    return (
      <>
        {!allowed && (
          <p className="note">
            {name} heeft reserveren uitgezet. Laat het {name} weten als je dit blik hebt.
          </p>
        )}
        {other && <p className="note">{other} heeft 'm al geregeld.</p>}
        {mine && <p className="note">Geregeld ✓ · Jij neemt deze mee.</p>}
        {error && (
          <div className="error" role="alert">
            {error}
          </div>
        )}
        {allowed && !other && (
          <button
            type="button"
            className={mine ? 'btn btn--outline' : 'btn'}
            disabled={pending}
            onClick={() => void toggle(item.id, !mine)}
          >
            {pending ? 'Even wachten…' : mine ? 'Toch niet · reservering intrekken' : 'Ik heb deze'}
          </button>
        )}
      </>
    );
  };
  const backToList = () => {
    scan.stop();
    setScanning(false);
    setSelectedId(null);
    setAskName(null);
    setError(null);
  };

  return (
    <div className={preview ? 'friend' : 'friend friend--page'}>
      {scanning ? (
        <PublicScan
          owner={name}
          scan={scan}
          hidden={askName !== null || !!selected}
          onBack={backToList}
          onDetail={(result) => {
            setError(null);
            setSelectedId(result.wanted!.id);
          }}
          reservation={(result) => reservation(result.wanted!, result.allowReservations)}
        />
      ) : (
        <>
          <div style={{ padding: '0 20px' }}>
            <div className="friend__bar">
              <div className="friend__url">
                {location.host}/{list.shareSlug}
              </div>
              {preview && (
                <button type="button" className="pill" style={{ height: 36, fontSize: 14 }} onClick={preview.onClose}>
                  Sluit voorbeeld
                </button>
              )}
            </div>
            {n === 0 ? (
              <h1 className="friend__hero">
                {name} heeft
                <br />
                <span>ze allemaal</span>
              </h1>
            ) : (
              <h1 className="friend__hero">
                {name} zoekt
                <br />
                <span>
                  nog {n} {n === 1 ? 'blik' : 'blikken'}
                </span>
              </h1>
            )}
            <div className="friend__intro">
              {n === 0
                ? 'De zoeklijst is leeg. Kom later nog eens kijken.'
                : list.allowReservations
                  ? `Kom je er een tegen? Open het blik en tik op “Ik heb deze”, dan weet ${name} dat jij hem meeneemt.`
                  : `Kom je er een tegen in de winkel of op vakantie? Laat het ${name} weten.`}
            </div>
            <button
              type="button"
              className="btn friend__scan"
              onClick={() => {
                setError(null);
                scan.start();
                setScanning(true);
              }}
            >
              Zoekt {name} dit blik?
            </button>
            {error && !selected && (
              <div className="error" role="alert">
                {error}
              </div>
            )}
          </div>

          {groups.map(({ cat, items }) => (
            <section key={cat.id} className="group">
              <h2 className="label" style={{ margin: 0, paddingBottom: 8 }}>
                {cat.name}
              </h2>
              <div className="rows">
                {items.map((m) => {
                  const mine = isMine(m.id, m.mine);
                  const other = m.reservedBy && !mine && !(preview && m.mine) ? m.reservedBy : null;
                  const status = other ? `${other} heeft 'm al geregeld` : mine ? 'Jij neemt deze mee' : null;
                  // Which one exactly: "uit Verenigde Staten · 710 ml".
                  const variant = [m.country && `uit ${countryLongName(m.country)}`, m.sizeMl && sizeLabel(m.sizeMl)]
                    .filter(Boolean)
                    .join(' · ');
                  return (
                    <button
                      key={m.id}
                      type="button"
                      className="row row--button friend__can"
                      aria-haspopup="dialog"
                      onClick={() => {
                        setError(null);
                        setSelectedId(m.id);
                      }}
                    >
                      <span className="row__thumb">
                        <CanImage imageId={m.imageId} empty="blank" />
                      </span>
                      <span className="row__body">
                        <span className="row__name">{m.name}</span>
                        {variant && <span className="row__sub">{variant}</span>}
                        {status && <span className="row__sub">{status}</span>}
                      </span>
                      <span className="friend__can-arrow" aria-hidden="true">
                        ›
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>
          ))}
        </>
      )}

      {selected && askName === null && (
        <Sheet label={selected.name} className="friend-can-modal" onClose={() => setSelectedId(null)}>
          {(close) => (
            <>
              <div className="friend-can-modal__header">
                <div>
                  <h2 className="sheet__name">{selected.name}</h2>
                  <div className="row__sub">
                    {[
                      list.categories.find((c) => c.id === selected.categoryId)?.name,
                      selected.country && `uit ${countryLongName(selected.country)}`,
                      selected.sizeMl && sizeLabel(selected.sizeMl),
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </div>
                </div>
                <button type="button" className="pill" onClick={close}>
                  Sluiten
                </button>
              </div>
              <ZoomableCanImage key={selected.id} imageId={selected.imageId} name={selected.name} />
              {scanning && (
                <p className="note">
                  Je bekijkt de gezochte variant: {wantedVariant(selected)}. Een toezegging geldt voor deze variant.
                </p>
              )}
              {reservation(selected, scanning ? scan.result!.data!.allowReservations : list.allowReservations)}
            </>
          )}
        </Sheet>
      )}

      {askName !== null && (
        <NameSheet
          onClose={() => setAskName(null)}
          onSubmit={(friendName) => {
            setFriendName(friendName);
            void toggle(askName, true, friendName);
          }}
          owner={name}
        />
      )}
    </div>
  );
}

function NameSheet({
  owner,
  onSubmit,
  onClose,
}: {
  owner: string;
  onSubmit: (name: string) => void;
  onClose: () => void;
}) {
  const [value, setValue] = useState('');
  return (
    <Sheet label="Hoe heet je?" onClose={onClose}>
      {(close) => (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!value.trim()) return;
            onSubmit(value.trim());
            close();
          }}
        >
          <div className="label label--green">Ik heb deze</div>
          <h2 className="sheet__name">Hoe heet je?</h2>
          <div className="note">Dan weet {owner} van wie het blik komt. We onthouden je naam op dit apparaat.</div>
          <input
            className="input"
            style={{ marginTop: 16 }}
            data-autofocus
            maxLength={60}
            autoComplete="given-name"
            placeholder="Je voornaam"
            aria-label="Je naam"
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
          <button type="submit" className="btn" style={{ marginTop: 16 }} disabled={!value.trim()}>
            Geregeld
          </button>
        </form>
      )}
    </Sheet>
  );
}
